import React, { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Award, Download, Printer, Medal } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import Panda from "../components/Panda";
import { useProgress } from "../context/ProgressContext";
import { GOAL_TIERS, goalStreakWeeks } from "../lib/goalStreak";

// Printable certificate for a goal-streak badge (Bronze 4 / Silver 8 / Gold 12 weeks).
const BadgeCertificate = () => {
  const { childId, tier } = useParams();
  const { findChild } = useProgress();
  const child = findChild(childId);
  const t = GOAL_TIERS.find((x) => x.tier === tier);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!child || !t) {
    return <div className="p-12 text-center" data-testid="badge-cert-missing">Badge not found. <Link to="/certificates" className="text-[#1b8bc9] underline">Back to the certificate wall</Link></div>;
  }
  const weeks = goalStreakWeeks(child.stepLog, child.weeklyGoal);
  const earned = weeks >= t.minWeeks;
  const dateStr = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const download = async () => {
    setBusy(true);
    try {
      const a = document.createElement("a");
      a.href = await toPng(ref.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff", skipFonts: true });
      a.download = `${child.panda.name}-${t.tier}-goal-streak-certificate.png`;
      a.click();
      toast.success("Certificate image downloaded");
    } catch (e) { toast.error("Could not create the image", { description: e?.message }); } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#f7fdfe] min-h-[80vh] py-10 px-4" data-testid="badge-certificate-page">
      <style>{`@media print { header, footer, .no-print { display: none !important; } @page { size: landscape; margin: 10mm; } body { background: #fff; } }`}</style>
      <div className="max-w-5xl mx-auto mb-6 flex flex-wrap items-center justify-between gap-3 no-print">
        <Link to="/certificates" className="inline-flex items-center gap-1 font-bold text-[#1b8bc9] hover:text-[#e5356f]" data-testid="badge-cert-back"><ArrowLeft size={18} /> Certificate wall</Link>
        {earned ? (
          <div className="flex flex-wrap gap-2">
            <button onClick={download} disabled={busy} className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white inline-flex items-center gap-2 shadow disabled:opacity-60" data-testid="badge-cert-download"><Download size={18} /> {busy ? "Preparing..." : "Download image"}</button>
            <button onClick={() => window.print()} className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white inline-flex items-center gap-2 shadow" data-testid="badge-cert-print"><Printer size={18} /> Print</button>
          </div>
        ) : (
          <span className="rounded-full bg-[#fff8e1] text-[#5a4600] font-bold text-sm px-4 py-2" data-testid="badge-cert-locked">{weeks} of {t.minWeeks} weeks - keep hitting the weekly goal to unlock</span>
        )}
      </div>

      <div ref={ref} className={`max-w-5xl mx-auto bg-white relative overflow-hidden rounded-3xl ${earned ? "" : "opacity-60 grayscale"}`} data-testid="badge-certificate">
        <div className="absolute inset-3 border-[6px] rounded-2xl pointer-events-none" style={{ borderColor: t.color }} />
        <div className="absolute inset-6 border-2 rounded-xl pointer-events-none" style={{ borderColor: t.color, opacity: 0.35 }} />
        {["top-8 left-8", "top-8 right-8", "bottom-8 left-8", "bottom-8 right-8"].map((pos) => <span key={pos} className={`absolute ${pos} w-6 h-6 rounded-full`} style={{ background: t.color }} />)}
        <div className="relative px-10 sm:px-20 py-14 text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="italic font-bold text-[#1b8bc9] text-lg" style={{ fontFamily: "Literata, serif" }}>Teach your child</span>
            <span className="font-logo text-2xl">{["C", "r", "e", "d", "i", "t"].map((l, i) => <span key={i} style={{ color: ["#e5356f", "#f7931e", "#63c79a", "#1b8bc9", "#7d5fb2", "#f3c932"][i] }}>{l}</span>)}</span>
          </div>
          <p className="mt-6 text-xs uppercase tracking-[0.4em] font-bold text-gray-500">{t.label} goal streak award</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }}>This certifies that</h1>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-8">
            <Panda size={150} accessory="crown" scarf={child.panda.scarf} item="coin" />
            <div>
              <p className="font-logo text-6xl sm:text-7xl leading-none" style={{ color: t.color }} data-testid="badge-cert-child">{child.panda.name}</p>
              <div className="h-1.5 w-full rounded-full mt-3" style={{ background: t.color }} />
            </div>
          </div>
          <p className="mt-8 text-lg text-gray-700">hit the weekly lesson goal of {child.weeklyGoal} step{child.weeklyGoal === 1 ? "" : "s"} a week</p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }} data-testid="badge-cert-weeks">{Math.max(weeks, earned ? t.minWeeks : weeks)} weeks in a row</h2>
          <p className="mt-2 text-gray-600">Consistency builds credit - and character.</p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 items-end text-sm">
            <div><p className="font-bold text-[#0c4a6e] border-t-2 border-gray-300 pt-2">{dateStr}</p><p className="text-gray-500">Date</p></div>
            <div className="flex justify-center"><span className="w-24 h-24 rounded-full flex flex-col items-center justify-center text-white shadow-lg" style={{ background: t.color }}><Medal size={30} /><span className="text-[10px] font-bold uppercase mt-1">{t.label}</span></span></div>
            <div><p className="font-bold text-[#0c4a6e] border-t-2 border-gray-300 pt-2">Ken Bossard, MBA</p><p className="text-gray-500">Author, Teach Your Child Credit</p></div>
          </div>
          <Award className="absolute bottom-6 right-8 text-gray-100" size={64} />
        </div>
      </div>
    </div>
  );
};

export default BadgeCertificate;
